/**
 * MAPPA — la pagina della mappa del core (TS-124).
 *
 * Del core sono la mappa OpenStreetMap, con i tile vettoriali OFFLINE (un archivio PMTiles letto a
 * range da un host statico, senza tile server), i fondi, i confini ISTAT e il pannello dei livelli.
 * La UI dei punti è del cliente: la ricerca nella colonna di sinistra, il disegno di ogni punto e
 * il riquadro di dettaglio li dà il `mapContributor` del plugin abilitato. Senza, la mappa resta
 * senza punti: una company che usa la mappa ha sempre un plugin con la sua UI.
 *
 * `VITE_MAP_PMTILES_URL` è l'URL dell'archivio; accanto stanno i font (`fonts/`) e lo sprite
 * (`sprites/v4/`) dello stile. Li prepara scripts/map-assets.sh, che è l'unico a sapere quale area
 * e quale zoom contiene l'archivio: la pagina inquadra quello che dicono i metadati dell'archivio.
 * Senza la variabile, o con un archivio che non risponde, la pagina resta nel menu e lo dice
 * invece di mostrare una mappa vuota.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LngLatBounds, Map as MapLibreMap, Marker, NavigationControl, Popup, addProtocol, setWorkerUrl,
} from 'maplibre-gl';
import type { GeoJSONSource, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?url';
import { PMTiles, Protocol } from 'pmtiles';
import { layers, namedFlavor } from '@protomaps/basemaps';
import type { MapContributor, MapItem } from '@crm/contracts';
import { LayersGlyph } from '../../components/icons';
import { istatAreaApi } from '../../entities/istatArea/istatAreaApi';
import type { IstatAreaGeometry } from '../../entities/istatArea/istatAreaTypes';
import { useMapContributor } from '../../plugin/useMapContributor';

/** La geometria di un poligono come la vuole una sorgente GeoJSON di MapLibre. */
type GeoJSONGeometry = { type: 'Polygon' | 'MultiPolygon'; coordinates: number[][][] | number[][][][] };

/** L'URL dell'archivio PMTiles; vuoto se le mappe offline non sono configurate. */
const MAP_PMTILES_URL = import.meta.env.VITE_MAP_PMTILES_URL ?? '';

/** Quanto ci si può spostare oltre l'area dell'archivio, in gradi: fuori non ci sono tile. */
const BOUNDS_MARGIN = 4;

/**
 * I fondi fra cui si sceglie: le varianti dello stile Protomaps. Sono tutte sullo stesso archivio,
 * cambiano solo i colori, e ognuna ha il suo sprite fra quelli scaricati da scripts/map-assets.sh.
 */
const FLAVORS = ['light', 'dark', 'white', 'grayscale', 'black'] as const;
type Flavor = typeof FLAVORS[number];

/** La sorgente del fondo: i livelli che non la usano sono sovrapposti nostri, e sopravvivono. */
const BASE_SOURCE = 'protomaps';

/**
 * Sorgente e livelli dei confini: la linea, e un riempimento trasparente per il nome al passaggio.
 * Una sorgente sola per tutti i tipi di confine: se ne vede uno alla volta, e cambiandolo si
 * sostituiscono i dati.
 */
const BOUNDARIES_SOURCE = 'boundaries';
const BOUNDARIES_LINE = 'boundaries-line';
const BOUNDARIES_FILL = 'boundaries-fill';

/** I confini fra cui si sceglie, col loro livello ISTAT (FILO-42). */
const BOUNDARY_LEVELS = { regions: 1, provinces: 2, municipalities: 3 } as const;
type Boundaries = keyof typeof BOUNDARY_LEVELS;

/**
 * I comuni sono quasi 8.000: si chiedono solo quelli dell'area inquadrata, e solo da questo zoom
 * in su, dove i confini si leggono. Allo zoom 9 una provincia sta tutta sullo schermo.
 */
const MUNICIPALITIES_MIN_ZOOM = 9;

/**
 * Quanto si allarga l'area inquadrata nel chiedere i comuni, in frazioni della sua larghezza e
 * altezza per lato: spostandosi di poco restano quelli già arrivati, senza una nuova richiesta.
 * Non di più: allo zoom minimo, in Lombardia, un'area più larga supera le 1.000 aree della REST.
 */
const MUNICIPALITIES_MARGIN = 0.25;

/** Il viola dei confini: si distingue dalle strade di tutte le varianti. */
const BOUNDARY_COLOR = '#7b1fa2';

/** I sovrapposti accesi nel pannello dei livelli. */
interface Overlays {
  /** I punti del plugin: marker DOM, non un livello della mappa. */
  points: boolean;
  /** I confini mostrati, uno solo alla volta; `null` se nessuno. */
  boundaries: Boundaries | null;
  /** Il nome dell'area al passaggio del mouse: spento di default, si sovrappone ai punti. */
  boundaryNames: boolean;
}

/** I livelli della mappa che si accendono e spengono coi confini. */
const BOUNDARY_LAYERS = [BOUNDARIES_LINE, BOUNDARIES_FILL];

type BoundariesGeoJson = ReturnType<typeof boundariesGeoJson>;

// Regioni e province non cambiano e pesano qualche MB: si chiedono una volta sola per tutta la
// sessione, anche se si esce dalla pagina e ci si torna. Una lettura fallita si riprova la volta dopo.
const boundariesCache = new Map<Boundaries, Promise<BoundariesGeoJson>>();

function loadBoundaries(boundaries: Exclude<Boundaries, 'municipalities'>) {
  let cached = boundariesCache.get(boundaries);
  if (!cached) {
    cached = istatAreaApi.geometries(BOUNDARY_LEVELS[boundaries]).then(boundariesGeoJson);
    cached.catch(() => boundariesCache.delete(boundaries));
    boundariesCache.set(boundaries, cached);
  }
  return cached;
}

/** Le aree come GeoJSON, ognuna col suo nome e, se c'è (le province), la sigla. */
function boundariesGeoJson(geometries: IstatAreaGeometry[]) {
  return {
    type: 'FeatureCollection' as const,
    features: geometries.map((g) => ({
      type: 'Feature' as const,
      geometry: g.geometry as GeoJSONGeometry,
      properties: { name: g.abbreviation ? `${g.name} (${g.abbreviation})` : g.name },
    })),
  };
}

/** Nessun confine: la sorgente resta, vuota. */
const NO_BOUNDARIES: BoundariesGeoJson = { type: 'FeatureCollection', features: [] };

/**
 * I comuni di una mappa: l'area per cui sono arrivati, per non richiederli finché ci si sposta
 * lì dentro, e il numero dell'ultima richiesta, perché una risposta superata non si disegni.
 */
interface MunicipalitiesState {
  loaded: LngLatBounds | null;
  request: number;
}

/** L'area inquadrata allargata di {@link MUNICIPALITIES_MARGIN} per lato, dentro i limiti del mondo. */
function widenedBounds(bounds: LngLatBounds): LngLatBounds {
  const dLng = (bounds.getEast() - bounds.getWest()) * MUNICIPALITIES_MARGIN;
  const dLat = (bounds.getNorth() - bounds.getSouth()) * MUNICIPALITIES_MARGIN;

  return new LngLatBounds(
    [Math.max(bounds.getWest() - dLng, -180), Math.max(bounds.getSouth() - dLat, -90)],
    [Math.min(bounds.getEast() + dLng, 180), Math.min(bounds.getNorth() + dLat, 90)]);
}

/** Vero se l'area interna sta tutta dentro quella esterna. */
function containsBounds(outer: LngLatBounds, inner: LngLatBounds) {
  return outer.contains(inner.getSouthWest()) && outer.contains(inner.getNorthEast());
}

// La preparazione di MapLibre si fa una volta per tutta l'app: worker e protocolli stanno in un
// registro globale, e una seconda registrazione sostituirebbe la prima senza dirlo.
let protocol: Protocol | null = null;

function setupMapLibre(): Protocol {
  if (protocol) return protocol;
  // MapLibre cerca il worker accanto al proprio file (import.meta.url), ma Vite lo sposta: in
  // sviluppo in node_modules/.vite/deps, in build dentro un chunk dell'app, e lì il worker non
  // c'è. Con ?url Vite lo pubblica come file a sé, ed è autosufficiente: non importa altro.
  setWorkerUrl(maplibreWorkerUrl);
  protocol = new Protocol();
  addProtocol('pmtiles', protocol.tile);
  return protocol;
}

/** Lo stile della mappa: Protomaps in italiano nella variante scelta, sull'archivio e sugli asset accanto. */
function mapStyle(archiveUrl: string, flavor: Flavor): StyleSpecification {
  // La cartella dell'archivio, senza barra finale.
  const base = new URL('.', archiveUrl).href.replace(/\/$/, '');

  return {
    version: 8,
    glyphs: `${base}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${base}/sprites/v4/${flavor}`,
    sources: {
      [BASE_SOURCE]: {
        type: 'vector',
        url: `pmtiles://${archiveUrl}`,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      },
    },
    layers: layers(BASE_SOURCE, namedFlavor(flavor), { lang: 'it' }),
  };
}

/**
 * Lo stile nuovo con i sovrapposti di quello vecchio: cambiando fondo, punti e confini non devono
 * sparire. Dei vecchi si tengono le sorgenti diverse da quella del fondo e i livelli che le usano,
 * in cima, nell'ordine in cui erano.
 */
function keepOverlays(previous: StyleSpecification | undefined, next: StyleSpecification): StyleSpecification {
  if (!previous) return next;

  const sources = Object.fromEntries(
    Object.entries(previous.sources).filter(([id]) => id !== BASE_SOURCE));
  const overlays = previous.layers.filter((l) => 'source' in l && l.source !== BASE_SOURCE);

  return { ...next, sources: { ...next.sources, ...sources }, layers: [...next.layers, ...overlays] };
}

export function MapPage() {
  const { t } = useTranslation();
  const contributor = useMapContributor();
  // I punti che la ricerca del plugin ha consegnato: la mappa disegna esattamente questi.
  const [items, setItems] = useState<MapItem[]>([]);

  return (
    <div className="map-root">
      <div className="map-page">
        {/* Il titolo sta nella colonna dei filtri: la mappa parte dall'alto. */}
        <aside className="map-filters list-card" aria-label={t('map.page.filters')}>
          <h1 className="page-title map-filters__title">{t('map.page.title')}</h1>
          {contributor && <contributor.search onItems={setItems} />}
        </aside>
        <div className="map-canvas list-card">
          {MAP_PMTILES_URL
            ? <MapCanvas pmtilesUrl={MAP_PMTILES_URL} contributor={contributor} items={items} />
            : <MapUnavailable />}
        </div>
      </div>
    </div>
  );
}

/**
 * I confini sulla mappa. La prima volta crea sorgente e livelli: la linea viola, e sotto un
 * riempimento trasparente che al passaggio del mouse dice il nome dell'area, se acceso nel
 * pannello; i punti, che sono marker DOM, restano sopra e cliccabili. Le volte dopo sostituisce
 * solo i dati.
 */
function showBoundaries(map: MapLibreMap, data: BoundariesGeoJson,
  visible: () => boolean, showNames: () => boolean) {
  if (!map.isStyleLoaded()) {
    map.once('idle', () => showBoundaries(map, data, visible, showNames));
    return;
  }

  const source = map.getSource(BOUNDARIES_SOURCE);
  if (source) {
    (source as GeoJSONSource).setData(data);
    return;
  }

  const visibility = visible() ? 'visible' : 'none';

  map.addSource(BOUNDARIES_SOURCE, { type: 'geojson', data });
  map.addLayer({
    id: BOUNDARIES_FILL,
    type: 'fill',
    source: BOUNDARIES_SOURCE,
    layout: { visibility },
    paint: { 'fill-color': BOUNDARY_COLOR, 'fill-opacity': 0 },
  });
  map.addLayer({
    id: BOUNDARIES_LINE,
    type: 'line',
    source: BOUNDARIES_SOURCE,
    layout: { visibility },
    paint: { 'line-color': BOUNDARY_COLOR, 'line-width': 2, 'line-opacity': 0.85 },
  });

  const tooltip = new Popup({ closeButton: false, closeOnClick: false, className: 'map-tooltip' });
  map.on('mousemove', BOUNDARIES_FILL, (e) => {
    const name = String(e.features?.[0]?.properties?.name ?? '');
    if (!name || !showNames()) {
      tooltip.remove();
      return;
    }
    tooltip.setLngLat(e.lngLat).setText(name).addTo(map);
  });
  map.on('mouseleave', BOUNDARIES_FILL, () => tooltip.remove());
}

/**
 * I confini scelti nel pannello, chiesti la prima volta che servono. Se nel frattempo la scelta è
 * cambiata, o la mappa non c'è più, la risposta arrivata in ritardo non si disegna.
 *
 * I comuni invece seguono la mappa: si chiamano a ogni spostamento, e chiedono solo quelli
 * dell'area inquadrata, quando non sono già arrivati. Sotto {@link MUNICIPALITIES_MIN_ZOOM} non
 * se ne mostra nessuno.
 */
function applyBoundaries(map: MapLibreMap, overlays: () => Overlays, isCurrent: () => boolean,
  municipalities: MunicipalitiesState) {
  const boundaries = overlays().boundaries;
  const show = (data: BoundariesGeoJson) => showBoundaries(map, data,
    () => overlays().boundaries !== null, () => overlays().boundaryNames);

  if (boundaries !== 'municipalities') {
    // I comuni arrivati non sono più sulla mappa, e una loro risposta in viaggio non vale più.
    municipalities.loaded = null;
    municipalities.request++;
    if (!boundaries) return;

    loadBoundaries(boundaries).then((data) => {
      if (isCurrent() && overlays().boundaries === boundaries) show(data);
    }, (e: unknown) => console.error(`Confini non letti: ${boundaries}`, e));
    return;
  }

  if (map.getZoom() < MUNICIPALITIES_MIN_ZOOM) {
    municipalities.loaded = null;
    municipalities.request++;
    show(NO_BOUNDARIES);
    return;
  }

  const view = map.getBounds();
  if (municipalities.loaded && containsBounds(municipalities.loaded, view)) return;

  const area = widenedBounds(view);
  const request = ++municipalities.request;

  istatAreaApi.geometries(BOUNDARY_LEVELS.municipalities, {
    minLatitude: area.getSouth(), minLongitude: area.getWest(),
    maxLatitude: area.getNorth(), maxLongitude: area.getEast(),
  }).then((geometries) => {
    if (!isCurrent() || request !== municipalities.request) return;
    municipalities.loaded = area;
    show(boundariesGeoJson(geometries));
  }, (e: unknown) => console.error('Confini dei comuni non letti', e));
}

function MapUnavailable() {
  const { t } = useTranslation();

  return (
    <div className="map-unavailable">
      <div className="map-unavailable__title">{t('map.page.unavailable')}</div>
      <div className="map-unavailable__hint">{t('map.page.unavailableHint')}</div>
    </div>
  );
}

function MapCanvas({ pmtilesUrl, contributor, items }: {
  pmtilesUrl: string;
  /** La UI dei punti del plugin abilitato; `null` = nessun punto. */
  contributor: MapContributor | null;
  /** I punti da disegnare, come li ha consegnati la ricerca del plugin. */
  items: MapItem[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  // La variante che la mappa ha davvero: alla creazione è il chiaro, qualunque sia la scelta.
  const appliedFlavorRef = useRef<Flavor>('light');
  const [failed, setFailed] = useState(false);
  // Vero quando l'archivio ha risposto e la mappa ha la sua area: prima i marker non si mettono.
  const [ready, setReady] = useState(false);
  const [flavor, setFlavor] = useState<Flavor>('light');
  const [selected, setSelected] = useState<MapItem | null>(null);
  // I marker sulla mappa, da togliere quando arrivano punti nuovi.
  const markersRef = useRef<Marker[]>([]);
  // I punti si inquadrano una volta sola, al primo elenco non vuoto: dopo, la vista è di chi guarda.
  const fittedRef = useRef(false);
  const [overlays, setOverlays] = useState<Overlays>({ points: true, boundaries: null, boundaryNames: false });
  // L'ultima scelta dei sovrapposti, per chi crea un livello fuori dal ciclo di React.
  const overlaysRef = useRef(overlays);
  const municipalitiesRef = useRef<MunicipalitiesState>({ loaded: null, request: 0 });
  // Sotto lo zoom dei comuni il pannello dice di avvicinarsi, invece di una mappa senza confini.
  const [belowMunicipalitiesZoom, setBelowMunicipalitiesZoom] = useState(true);

  useEffect(() => {
    if (!containerRef.current) return;

    // Gli URL devono essere assoluti: MapLibre risolve tile, glifi e sprite dal suo worker, dove un
    // percorso relativo non ha una pagina a cui riferirsi.
    const archiveUrl = new URL(pmtilesUrl, window.location.href).href;
    const archive = new PMTiles(archiveUrl);
    // Registrato nel protocollo, l'archivio è lo stesso da cui si leggono i tile: l'intestazione
    // letta qui non si richiede una seconda volta.
    setupMapLibre().add(archive);

    // Il fondo iniziale è il chiaro: la variante scelta dopo la applica l'effetto qui sotto.
    const map = new MapLibreMap({ container: containerRef.current, style: mapStyle(archiveUrl, 'light') });
    mapRef.current = map;
    appliedFlavorRef.current = 'light';
    // Zoom a sinistra e livelli a destra: a destra si apre anche il dettaglio.
    map.addControl(new NavigationControl({ showCompass: false }), 'top-left');

    let cancelled = false;
    // Una mappa nuova non ha comuni: l'area di quella vecchia non vale.
    municipalitiesRef.current = { loaded: null, request: 0 };
    const applyHere = () => applyBoundaries(map, () => overlaysRef.current,
      () => mapRef.current === map, municipalitiesRef.current);

    // L'area la dice l'archivio: la pagina non sa se contiene l'Italia o una sola regione. I punti
    // del plugin arrivano dopo, e se ce ne sono si inquadrano loro.
    fittedRef.current = false;
    archive.getHeader()
      .then((h) => {
        if (cancelled) return;
        map.setMaxBounds([[h.minLon - BOUNDS_MARGIN, h.minLat - BOUNDS_MARGIN],
          [h.maxLon + BOUNDS_MARGIN, h.maxLat + BOUNDS_MARGIN]]);
        map.fitBounds([[h.minLon, h.minLat], [h.maxLon, h.maxLat]], { animate: false });

        // Di default nessun confine: ci sono solo se scelti prima che la mappa fosse pronta.
        applyHere();
        setBelowMunicipalitiesZoom(map.getZoom() < MUNICIPALITIES_MIN_ZOOM);

        // I comuni seguono la mappa; gli altri confini sono già tutti sulla mappa.
        map.on('moveend', () => {
          setBelowMunicipalitiesZoom(map.getZoom() < MUNICIPALITIES_MIN_ZOOM);
          if (overlaysRef.current.boundaries === 'municipalities') applyHere();
        });

        setReady(true);
      }, (e: unknown) => {
        console.error('Archivio delle mappe offline non leggibile', e);
        setFailed(true);
      });

    return () => {
      cancelled = true;
      mapRef.current = null;
      setReady(false);
      map.remove();
    };
  }, [pmtilesUrl]);

  // I punti del plugin: un marker DOM per punto, col disegno che dà il plugin. Arriva un elenco
  // nuovo, si tolgono i marker vecchi e si mettono i nuovi; il clic apre il dettaglio.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !contributor) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = items.map((item) => {
      const element = contributor.marker(item);
      element.style.display = overlaysRef.current.points ? '' : 'none';
      element.style.cursor = 'pointer';
      element.addEventListener('click', (e) => {
        // Il clic è del punto, non della mappa sotto.
        e.stopPropagation();
        setSelected(item);
      });
      return new Marker({ element }).setLngLat([item.longitude, item.latitude]).addTo(map);
    });

    if (!fittedRef.current && items.length > 0) {
      fittedRef.current = true;
      const bounds = new LngLatBounds();
      items.forEach((i) => bounds.extend([i.longitude, i.latitude]));
      map.fitBounds(bounds, { padding: 40, maxZoom: 14, animate: false });
    }

    // Il punto aperto nel dettaglio resta solo se c'è ancora fra quelli disegnati.
    setSelected((s) => (s && items.some((i) => i.id === s.id) ? s : null));
  }, [items, contributor, ready]);

  // Accende e spegne i sovrapposti: i confini sono livelli della mappa, i punti marker DOM.
  useEffect(() => {
    overlaysRef.current = overlays;
    markersRef.current.forEach((m) => {
      m.getElement().style.display = overlays.points ? '' : 'none';
    });
    const map = mapRef.current;
    if (!map) return;

    const visibility = overlays.boundaries ? 'visible' : 'none';
    BOUNDARY_LAYERS.filter((id) => map.getLayer(id))
      .forEach((id) => map.setLayoutProperty(id, 'visibility', visibility));
  }, [overlays]);

  // Cambio di confini: i dati del tipo scelto prendono il posto di quelli di prima.
  useEffect(() => {
    const map = mapRef.current;
    if (map) applyBoundaries(map, () => overlaysRef.current, () => mapRef.current === map,
      municipalitiesRef.current);
  }, [overlays.boundaries]);

  // Cambio di fondo: si sostituisce lo stile tenendo i sovrapposti, senza ricreare la mappa, che
  // perderebbe posizione e zoom.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || appliedFlavorRef.current === flavor) return;

    appliedFlavorRef.current = flavor;
    const archiveUrl = new URL(pmtilesUrl, window.location.href).href;
    map.setStyle(mapStyle(archiveUrl, flavor), { transformStyle: keepOverlays });
  }, [flavor, pmtilesUrl]);

  if (failed) return <MapUnavailable />;

  return (
    <>
      <div ref={containerRef} className="map-canvas__map" />
      <LayersPanel flavor={flavor} onFlavor={setFlavor} overlays={overlays} onOverlays={setOverlays}
        belowMunicipalitiesZoom={belowMunicipalitiesZoom} />
      {/* Il riquadro in alto a destra è del core, il contenuto del plugin. */}
      {selected && contributor && (
        <div className="map-detail">
          <contributor.detail item={selected} onClose={() => setSelected(null)} />
        </div>
      )}
    </>
  );
}

/** Il pannello dei livelli, chiuso su un pulsante: fondi e sovrapposti. */
function LayersPanel({ flavor, onFlavor, overlays, onOverlays, belowMunicipalitiesZoom }: {
  flavor: Flavor;
  onFlavor: (f: Flavor) => void;
  overlays: Overlays;
  onOverlays: (o: Overlays) => void;
  /** Vero se la mappa è troppo lontana per mostrare i comuni. */
  belowMunicipalitiesZoom: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="map-layers">
      <button type="button" className="map-layers__toggle" aria-expanded={open}
        title={t('map.layers.title')} onClick={() => setOpen((o) => !o)}>
        <LayersGlyph />
      </button>
      {open && (
        <div className="map-layers__panel">
          <div className="map-layers__heading">{t('map.layers.base')}</div>
          {FLAVORS.map((f) => (
            <label key={f} className="map-layers__option">
              <input type="radio" name="map-flavor" checked={flavor === f} onChange={() => onFlavor(f)} />
              {t(`map.layers.flavors.${f}`)}
            </label>
          ))}
          <div className="map-layers__heading map-layers__heading--next">{t('map.layers.overlays')}</div>
          <label className="map-layers__option">
            <input type="checkbox" checked={overlays.points}
              onChange={(e) => onOverlays({ ...overlays, points: e.target.checked })} />
            {t('map.layers.points')}
          </label>
          <div className="map-layers__heading map-layers__heading--next">{t('map.layers.boundaries.title')}</div>
          {/* Uno solo alla volta: confini di livelli diversi insieme si confondono. */}
          {([null, ...Object.keys(BOUNDARY_LEVELS)] as (Boundaries | null)[]).map((b) => (
            <label key={b ?? 'none'} className="map-layers__option">
              <input type="radio" name="map-boundaries" checked={overlays.boundaries === b}
                onChange={() => onOverlays({ ...overlays, boundaries: b })} />
              {t(`map.layers.boundaries.${b ?? 'none'}`)}
            </label>
          ))}
          {overlays.boundaries === 'municipalities' && belowMunicipalitiesZoom && (
            <div className="map-layers__hint">{t('map.layers.boundaries.municipalitiesZoomHint')}</div>
          )}
          <label className="map-layers__option">
            <input type="checkbox" checked={overlays.boundaryNames} disabled={!overlays.boundaries}
              onChange={(e) => onOverlays({ ...overlays, boundaryNames: e.target.checked })} />
            {t('map.layers.boundaries.names')}
          </label>
        </div>
      )}
    </div>
  );
}
